import { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { FolderKanban, Boxes, Contact, LogOut, Menu, ChevronDown, Ban, ArrowUpRight, Pencil, PencilRuler, Home, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useActiveProject } from "@/context/ProjectContext";
import { isAdminUser } from "@/lib/roles";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import NoAccess from "@/pages/NoAccess";
import HokusaiDecor from "@/components/HokusaiDecor";
import GlobalSearch from "@/components/GlobalSearch";

const NAV = [
  { to: "/admin", label: "Proyectos", icon: FolderKanban, end: true, testid: "admin-nav-proyectos" },
  { to: "/admin/vivienda-modelo", label: "Vivienda modelo", icon: Home, end: false, testid: "admin-nav-vivienda-modelo" },
  { to: "/admin/disenos", label: "Diseños", icon: PencilRuler, end: false, testid: "admin-nav-disenos" },
  { to: "/admin/recursos", label: "Gestión de recursos", icon: Boxes, end: false, testid: "admin-nav-recursos" },
  { to: "/admin/crm", label: "CRM · Contactos", icon: Contact, end: false, testid: "admin-nav-crm" },
  { to: "/admin/empresa", label: "Datos de empresa", icon: Building2, end: false, testid: "admin-nav-empresa" },
];

function ActiveProjectBar() {
  const { activeProject, setActiveProject, clearActiveProject } = useActiveProject();
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/projects", { params: { role: "administrador" } })
      .then((r) => setProjects(r.data || []))
      .catch(() => {});
  }, []);

  const pick = (p) => { setActiveProject(p); navigate(`/admin/proyectos/${p.project_id}`); };
  const none = () => { clearActiveProject(); navigate("/admin/recursos"); };

  return (
    <div className="flex items-center justify-between gap-3 h-14 px-4 sm:px-6 lg:px-10 border-b border-border/70 bg-background/85 backdrop-blur-md sticky top-16 lg:top-0 z-30" data-testid="active-project-bar">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`h-2 w-2 rounded-full shrink-0 ${activeProject ? "bg-primary" : "bg-muted-foreground/40"}`} />
        <span className="text-xs text-muted-foreground hidden sm:inline">Proyecto activo:</span>
        <span className={`text-sm font-semibold truncate ${activeProject ? "" : "text-muted-foreground"}`} data-testid="active-project-name">
          {activeProject ? activeProject.name : "Sin proyecto definido"}
        </span>
      </div>
      <div className="flex-1 flex justify-center px-2 sm:px-4" data-testid="global-search-wrap">
        <GlobalSearch />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5 shrink-0" data-testid="active-project-switcher">
            <FolderKanban className="h-4 w-4" /> <span className="hidden sm:inline">Cambiar proyecto</span><span className="sm:hidden">Proyecto</span> <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onClick={none} data-testid="active-project-none">
            <Ban className="h-4 w-4 mr-2" /> Trabajar sin proyecto
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Elegir proyecto</DropdownMenuLabel>
          {projects.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">No hay proyectos todavía.</div>
          )}
          <div className="max-h-64 overflow-y-auto">
            {projects.map((p) => (
              <DropdownMenuItem key={p.project_id} onClick={() => pick(p)} data-testid={`active-project-option-${p.project_id}`}>
                <span className="truncate">{p.name}</span>
                {activeProject?.project_id === p.project_id && <span className="ml-auto h-2 w-2 rounded-full bg-primary" />}
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function SidebarBody({ user, logout, onNavigate, idSuffix = "" }) {
  const { activeProject } = useActiveProject();
  const initials = (user?.name || user?.email || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex flex-col h-full">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 px-5 h-16 border-b border-border/70 shrink-0" data-testid={`admin-sidebar-logo${idSuffix}`}>
        <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
        <span className="text-xl font-bold tracking-tight lowercase">mori</span>
      </Link>
      <div className="px-3 py-5 flex-1 overflow-y-auto">
        <div className="caption px-2 mb-2">Panel del administrador</div>
        <nav className="space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`${item.testid}${idSuffix}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4.5 w-4.5" /> {item.label}
            </NavLink>
          ))}
        </nav>

        {activeProject ? (
          <div className="mt-6" data-testid={`sidebar-active-project${idSuffix}`}>
            <div className="caption px-2 mb-2">Proyecto activo</div>
            <div className="rounded-xl border border-primary/25 bg-primary/5 p-3">
              <div className="text-sm font-semibold truncate">{activeProject.name}</div>
              {activeProject.location && <div className="text-xs text-muted-foreground truncate mt-0.5">{activeProject.location}</div>}
              <div className="mt-2.5 space-y-1">
                <Link
                  to={`/admin/proyectos/${activeProject.project_id}`}
                  onClick={onNavigate}
                  data-testid={`sidebar-open-project${idSuffix}`}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  <ArrowUpRight className="h-4 w-4" /> Abrir espacio del proyecto
                </Link>
                <Link
                  to={`/admin/proyectos/${activeProject.project_id}?tab=disenos`}
                  onClick={onNavigate}
                  data-testid={`sidebar-designs${idSuffix}`}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <PencilRuler className="h-4 w-4" /> Diseños
                </Link>
                <Link
                  to={`/admin/proyectos/${activeProject.project_id}?tab=ajustes`}
                  onClick={onNavigate}
                  data-testid={`sidebar-project-settings${idSuffix}`}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                >
                  <Pencil className="h-4 w-4" /> Ajustes del proyecto
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 px-2" data-testid={`sidebar-no-project${idSuffix}`}>
            <div className="caption mb-2">Sin proyecto</div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Trabajando sin proyecto. Disponibles: Gestión de recursos y CRM. Elige un proyecto arriba para abrir su espacio.
            </p>
          </div>
        )}
      </div>
      <div className="border-t border-border/70 p-3 shrink-0">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <Avatar className="h-9 w-9 border border-border shrink-0">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-accent text-olive text-sm font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.name || "Administrador"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive mt-1" onClick={logout} data-testid="admin-logout-button">
          <LogOut className="h-4 w-4" /> Cerrar sesión
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isAdminUser(user)) return <NoAccess title="Esta zona es solo para administradores" />;

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border/70 bg-card sticky top-0 h-screen">
        <SidebarBody user={user} logout={logout} />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden flex items-center justify-between h-16 px-4 border-b border-border/70 sticky top-0 bg-background/85 backdrop-blur-md z-40">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full -ml-2" data-testid="admin-mobile-menu-button" aria-label="Menú">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SheetTitle className="sr-only">Menú del administrador</SheetTitle>
              <SidebarBody user={user} logout={logout} onNavigate={() => setOpen(false)} idSuffix="-mobile" />
            </SheetContent>
          </Sheet>
          <Link to="/" className="flex items-center gap-2">
            <img src="/mori-logo.jpg" alt="Mori" className="h-8 w-8 rounded-lg object-cover" />
            <span className="text-lg font-bold tracking-tight lowercase">mori</span>
          </Link>
          <Avatar className="h-8 w-8 border border-border">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-accent text-olive text-xs font-semibold">
              {(user?.name || user?.email || "?")[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </header>

        <main className="relative flex-1 min-w-0">
          <HokusaiDecor />
          <div className="relative z-10">
            <ActiveProjectBar />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
